import React, { Suspense, useEffect, useMemo } from 'react';

import { loadEntryPoint, useRelayEnvironment } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';
import {
	AgentProfileCardResourced,
	type AgentProfileCardResourcedProps,
} from '@atlaskit/profilecard/agent-profile-card-resourced';
import {
	agentProfileCardEntryPoint,
} from '@atlassian/conversation-assistant-agent/common/ui/agent-profile-card-entrypoint';
import { AgentProfileCardSkeleton } from '@atlassian/conversation-assistant-agent/common/ui/AgentProfileCardSkeleton';
import { InternalEntryPointContainer } from '@atlassian/internal-entry-point-container';

export type AgentCardProps = AgentProfileCardResourcedProps;

function AgentCardRelay({ accountId, cloudId }: AgentCardProps): React.JSX.Element {
	const environment = useRelayEnvironment();

	const entryPointReference = useMemo(
		() =>
			loadEntryPoint(
				{ getEnvironment: () => environment },
				agentProfileCardEntryPoint,
				{ identityAccountId: accountId, cloudId },
			),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[accountId, cloudId],
	);

	useEffect(() => {
		return () => {
			entryPointReference.dispose?.();
		};
	}, [entryPointReference]);

	return (
		<Suspense fallback={<AgentProfileCardSkeleton />}>
			<InternalEntryPointContainer
				id="agent-profile-card"
				entryPointReference={entryPointReference}
				runtimeProps={{ cloudId }}
				fallback={<AgentProfileCardSkeleton />}
			/>
		</Suspense>
	);
}

/**
 * Profile card for a Rovo agent principal.
 *
 * Uses Relay entrypoint when gate is enabled, otherwise falls back to REST-based card.
 */
export function AgentCard(props: AgentCardProps): React.JSX.Element {
	if (fg('rovo_agent_profile_card_relay')) {
		return <AgentCardRelay {...props} />;
	}
	return <AgentProfileCardResourced {...props} />;
}
